<script setup>
import { ref } from 'vue'

const { min, max, step, label, name, model, data } = defineProps({
  label: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  model: {
    type: String,
  },
  data: {
    type: Object,
  },
})

const inputMin = ref(null)
const inputMax = ref(null)
const range = ref(data[model].range)

const minValue = name === 'duration' ? millisecondsToTime(min) : min
const maxValue = name === 'duration' ? millisecondsToTime(max) : max

function millisecondsToTime(ms) {
  const totalSeconds = Math.floor(ms / 100000)
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    '0'
  )
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

function timeToMilliseconds(time) {
  if (!time) {
    return 0
  }
  const [hours, minutes, seconds] = time.split(':').map(Number)
  const totalSeconds = hours * 3600 + minutes * 60 + seconds
  const milliseconds = totalSeconds * 100000
  return milliseconds
}

const onInput = ({ target }) => {
  if (target.name === 'min') {
    data[model].minValue =
      name === 'duration' ? timeToMilliseconds(target.value) : target.value
    inputMin.value = target.value
  } else {
    data[model].maxValue =
      name === 'duration' ? timeToMilliseconds(target.value) : target.value
    inputMax.value = target.value
  }
}
watch(range, (newRange) => {
  if (data[model].range !== newRange) {
    data[model].range = newRange
  }
})

watch(
  () => data[model],
  (newValue) => {
    if (!newValue.minValue && !newValue.maxValue) {
      inputMin.value = null
      inputMax.value = null
      range.value = newValue.range
    }
    if (newValue.minValue && !inputMin.value) {
      inputMin.value = newValue.minValue
    }
    if (newValue.maxValue && !inputMax.value) {
      inputMax.value = newValue.maxValue
    }
    if (newValue.range && !range.value) {
      range.value = newValue.range
    }
  },
  { deep: true }
)
</script>
<template>
  <label class="font-semibold">{{ label }}</label>
  <div class="w-full">
    <div class="flex items-center">
      <input
        id="range"
        type="checkbox"
        class="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
        v-model="range"
        @change="onCheck"
      />
      <label for="range" class="ms-2 text-sm font-medium text-gray-900">
        {{ $t('advanced_search.specify_range') }}
      </label>
    </div>
    <div class="flex justify-between w-full gap-8">
      <div class="flex flex-col w-full">
        <span class="text-primary-100" v-if="!range"
          >(Min = {{ minValue }}, Max = {{ maxValue }})</span
        >
        <span class="text-primary-100" v-if="range"
          >(Min = {{ minValue }})</span
        >
        <input
          class="w-full text-gray-600"
          v-model="inputMin"
          :step="step"
          name="min"
          @input="onInput"
        />
      </div>
      <div class="flex flex-col w-full">
        <span class="text-primary-100 text-white" v-if="!range">Not empty</span>
        <span class="text-primary-100" v-if="range"
          >(Max = {{ maxValue }})</span
        >
        <input
          class="w-full text-gray-600 disabled:bg-gray-200"
          :disabled="!range"
          v-model="inputMax"
          :step="step"
          name="max"
          @input="onInput"
        />
      </div>
    </div>
  </div>
</template>
<style scoped>
.custom-slider {
  --trackHeight: 0.35rem;
  --thumbRadius: 1rem;
}

/* style the input element with type "range" */
.custom-slider input[type='range'] {
  position: relative;
  appearance: none;
  background: none;
  border-radius: 999px;
  z-index: 0;
  height: 100%;
  pointer-events: none;
}

/* ::before element to replace the slider track */
.custom-slider input[type='range']::before {
  content: '';
  display: block;
  position: absolute;
  width: var(--ProgressPercent, 100%);
  height: 100%;
  background: rgba(242, 98, 36, 1);
  border-radius: 999px;
}

/* `::-webkit-slider-runnable-track` targets the track (background) of a range slider in chrome and safari browsers. */
.custom-slider input[type='range']::-webkit-slider-runnable-track {
  appearance: none;

  height: var(--trackHeight);
  border-radius: 999px;
}

.custom-slider input[type='range']::-webkit-slider-thumb {
  position: relative;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  background: black;
  border: 1px solid black;
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  z-index: 1;
}

/* `::-moz-range-track` targets the track (background) of a range slider in Mozilla Firefox. */
.custom-slider.default input[type='range']::-moz-range-track {
  appearance: none;
  height: var(--trackHeight);
  border-radius: 999px;
}

.custom-slider input[type='range']::-moz-range-thumb {
  position: relative;
  box-sizing: border-box;
  width: var(--thumbRadius);
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);

  border: 1px solid black;
  border-radius: 999px;
  pointer-events: all;
  appearance: none;
  z-index: 1;
}

.custom-slider.minmax {
  position: relative;
  height: var(--trackHeight);

  border-radius: 999px;
  margin: 0.5rem 0;
  --progressLeft: 0%;
  --progressRight: 0%;
}

.custom-slider .minmax-indicator {
  position: absolute;
  height: 100%;
  pointer-events: none;
  left: var(--thumbRadius);
  right: var(--thumbRadius);
}

.custom-slider .minmax-indicator::before {
  content: '';
  position: absolute;
  background: rgba(242, 98, 36, 1);
  height: 100%;
  left: var(--progressLeft);
  right: var(--progressRight);
}

.custom-slider.minmax input[type='range'] {
  position: absolute;
  width: calc(100% - var(--thumbRadius));
}

.custom-slider.minmax input[type='range'][name='max'] {
  left: var(--thumbRadius);
}

.custom-slider.minmax input[type='range']::-webkit-slider-runnable-track {
  background: none;
}

.custom-slider.minmax input[type='range']::before {
  display: none;
}

.minmax-inputs {
  margin: auto;

  display: flex;
  justify-content: space-around;
}
</style>
